import React from 'react';
import { TFunction } from 'i18next';
import { ListingCardListing } from './types';

// Symbols for the currencies a Jaakko estimate can be priced in (the property's
// local market currency). Unknown/missing codes fall back to euros.
const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '\u20AC',
  GBP: '\u00A3',
  USD: '$',
};

/**
 * The euro sign. Exported for the places that show a bare unit rather than a
 * formatted amount \u2014 a form label, an input adornment. Do not concatenate it onto
 * a number: the formatters below own placement.
 */
export const CURRENCY = '\u20AC';

/**
 * Formats a card price in the app-wide fi-FI grouping ("805 000"). Euros use
 * the Finnish suffix convention with a non-breaking space ("805 000 \u20AC", like
 * feed events and buyer criteria already do); other currencies keep their
 * symbol in front ("\u00A3805 000").
 */
export const formatCardPrice = (
  amount: number,
  currency: string = 'EUR',
): string => {
  const formatted = Math.round(amount).toLocaleString('fi-FI');
  const symbol = CURRENCY_SYMBOLS[currency] ?? CURRENCY;
  if (symbol === CURRENCY) return `${formatted}\u00A0${CURRENCY}`;
  return `${symbol}${formatted}`;
};

export type SellerPriceDisplay =
  | { kind: 'asking'; text: string }
  | { kind: 'estimate'; text: string }
  | { kind: 'estimating'; text: string }
  | { kind: 'unset'; text: string };

export const sellerEstimateInfoText = (
  kind: 'estimate' | 'estimating',
  t: TFunction,
): string =>
  kind === 'estimating'
    ? t(
        'listingCard.estimatingInfo',
        'Jaakko is estimating the price using AI and public market data. The estimate will be a guide, not an appraisal.',
      )
    : t(
        'listingCard.estimateInfo',
        "Jaakko has estimated this price using AI and public market data. It's a guide, not an appraisal.",
      );

type SellerPriceFields = Pick<
  ListingCardListing,
  | 'property_asking_price'
  | 'estimate_status'
  | 'estimated_price'
  | 'estimated_price_currency'
>;

/**
 * Resolves a seller's price consistently on cards and listing pages. A price
 * explicitly set by the seller always wins; a completed automatic estimate is
 * the fallback, followed by its pending state and finally the empty label.
 *
 * A stored 0 counts as no asking price, as it does everywhere else this field
 * is read (the tip preview, the search-result card, the feed event) and in
 * `formatPriceRange` below. No property is genuinely free, so a 0 is a typo or
 * a placeholder, and an estimate serves the reader better than "0 €".
 */
export const sellerPriceDisplay = (
  listing: SellerPriceFields,
  t: TFunction,
): SellerPriceDisplay => {
  const positivePrice = (value: unknown): number | null => {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    const price = Number(value);
    return Number.isFinite(price) && price > 0 ? price : null;
  };
  const asking = positivePrice(listing.property_asking_price);
  const estimate = positivePrice(listing.estimated_price);
  if (asking !== null) {
    return {
      kind: 'asking',
      text: formatCardPrice(asking),
    };
  }
  if (listing.estimate_status === 'completed' && estimate !== null) {
    return {
      kind: 'estimate',
      text: `~${formatCardPrice(
        estimate,
        listing.estimated_price_currency ?? 'EUR',
      )}`,
    };
  }
  if (listing.estimate_status === 'pending') {
    return {
      kind: 'estimating',
      text: t('listingCard.estimatingPrice', 'Estimating price…'),
    };
  }
  return {
    kind: 'unset',
    text: t('listingCard.noSetPrice', 'No set price'),
  };
};

/** Which size bounds a listing actually carries \u2014 see `sizeDisplay`. */
export type SizeDisplay =
  | { kind: 'range'; min: number; max: number }
  | { kind: 'min'; min: number }
  | { kind: 'max'; max: number }
  | { kind: 'none' };

/** The unit `short` collapses an amount into, and the collapsed number. */
const shortParts = (n: number): { value: string; unit: string } => {
  // Unit follows the rounded thousands so 999 500 reads "1 M", never "1 000 K".
  const thousands = Math.round(n / 1000);
  if (thousands >= 1000) {
    // One decimal, and never a trailing ",0": 2 000 000 reads "2 M", not "2,0 M".
    const millions = Math.round(n / 100_000) / 10;
    return { value: millions.toLocaleString('fi-FI'), unit: 'M' };
  }
  return { value: thousands.toLocaleString('fi-FI'), unit: 'k' };
};

/**
 * An unsigned, grouped amount. `short` collapses the figure to its unit — "220 K",
 * "1,2 M" — because a price range is three numbers wide and the card's footer
 * shares one line with Share and Suggest; every other surface keeps the exact
 * figure. Grouping is always fi-FI, never the browser's locale, so a large figure
 * reads "1 200 000" rather than "1,200,000". Kept private so the sign only ever
 * lands via `formatCardPrice` or `formatPriceRange`.
 */
const groupedAmount = (n: number, short = false): string => {
  if (!short || n < 1000) return n.toLocaleString('fi-FI');
  const { value, unit } = shortParts(n);
  return `${value}${unit}`;
};

/**
 * A price range as one label carrying a single trailing sign
 * ("260 000\u2013790 000 \u20AC"), or a one-sided bound ("\u2265 260 000 \u20AC"). '' when neither
 * bound is set. Shared by the buyer budget slot and the area-interest criteria
 * slot, which sit in the same place on their cards and must read identically.
 *
 * Euro-only: buyer criteria carry no currency of their own today. When the
 * backend starts sending one, take it as a parameter here.
 */
export const formatPriceRange = (
  min?: number,
  max?: number,
  short = false,
): string => {
  const amount = (n: number) => groupedAmount(n, short);
  if (short && min && max && min >= 1000 && max >= 1000) {
    const lo = shortParts(min);
    const hi = shortParts(max);
    // The unit rides on its number and is stated once when both bounds share
    // it: "220 - 400k", "1,1 - 1,2M". Across units each bound carries its own:
    // "220k - 1,1M". Spaces around the dash are non-breaking, since letting the
    // range split across two lines is the bug this card was fixed for.
    const head = lo.unit === hi.unit ? lo.value : `${lo.value}${lo.unit}`;
    return `${head}\u00A0\u2013\u00A0${hi.value}${hi.unit}\u00A0${CURRENCY}`;
  }
  if (min && max)
    return `${amount(min)}\u00A0\u2013\u00A0${amount(max)}\u00A0${CURRENCY}`;
  if (min) return `\u2265 ${amount(min)}\u00A0${CURRENCY}`;
  if (max) return `\u2264 ${amount(max)}\u00A0${CURRENCY}`;
  return '';
};

/**
 * A buyer's budget as one label, or undefined when they set none. Read from
 * `criteria` because the flat property_asking_price mirror only ever holds the
 * cap: a buyer with a range saw just their maximum, and one with only a minimum
 * saw nothing.
 */
export const budgetDisplay = (
  listing: ListingCardListing,
  short = false,
): string | undefined =>
  formatPriceRange(
    listing.criteria?.price_min,
    listing.criteria?.price_max ?? listing.property_asking_price,
    short,
  ) || undefined;

/**
 * A listing's rooms, preferring criteria so a buyer's wanted count never depends
 * on the mirror being fresh. `flexible` is buy-side only and has no meaning on a
 * sell listing, which states a real room count rather than an acceptable one.
 */
export const roomsDisplay = (
  listing: ListingCardListing,
): { rooms: string | number | undefined; flexible: boolean } => ({
  rooms: listing.criteria?.rooms_min ?? listing.property_rooms,
  flexible: !!(
    listing.criteria?.rooms_flexible ?? listing.property_rooms_flexible
  ),
});

/**
 * Decide what a listing's size can say. The could-buy form asks for a range and
 * a listing keeps both bounds, either of which may stand alone — so "max only"
 * has to be its own case and read "≤ 80 m²". Treating it as a minimum would
 * advertise the opposite of what the buyer asked for.
 *
 * The decision lives here, not in the cards, because the buyer card and the
 * small card render it with different markup and both got it wrong separately.
 */
export const sizeDisplay = (listing: ListingCardListing): SizeDisplay => {
  // criteria is the canonical source; the flat fields remain as the fallback for
  // sell listings, which have a real size rather than a wanted range.
  const min = listing.criteria?.size_min ?? listing.property_size;
  const max = listing.criteria?.size_max ?? listing.property_size_max;
  if (min && max) return { kind: 'range', min, max };
  if (min) return { kind: 'min', min };
  if (max) return { kind: 'max', max };
  return { kind: 'none' };
};

/**
 * Maps a property's renovation status to its localized label, falling back to
 * the raw status string for any unrecognized value.
 */
export const getRenovationLabel = (status: string, t: TFunction): string => {
  switch (status) {
    case 'recently_renovated':
      return t('listingCard.recentlyRenovated');
    // The could-buy form wrote a bare 'good' before it shared this vocabulary;
    // a handful of stored interests still carry it.
    case 'good':
    case 'good_condition':
      return t('listingCard.goodCondition');
    case 'needs_renovation':
      return t('listingCard.needsRenovation');
    default:
      return status;
  }
};

/**
 * The line a sparse card shows in place of a description. The owner is nudged
 * to add details; everyone else gets the prompt to contact the other side.
 */
export const connectHintText = (
  t: TFunction,
  isBuyer: boolean,
  isOwn: boolean,
): string => {
  if (isOwn && isBuyer)
    return t(
      'listingCard.ownBuyerAddDetails',
      'Add details – attract more sellers.',
    );
  if (isOwn)
    return t(
      'listingCard.ownSellerAddDetails',
      'Add details – attract more buyers.',
    );
  if (isBuyer)
    return t(
      'listingCard.connectBuyerForDetails',
      'A buyer is looking – suggest your home.',
    );
  return t(
    'listingCard.connectForDetails',
    'Interested seller – ask for more details',
  );
};

/**
 * Builds the ordered chip labels (floor, renovation, optional elevator, balcony)
 * shown on a listing/buyer card from whichever property fields are present.
 */
export const buildPropertyChips = (
  listing: ListingCardListing,
  t: TFunction,
  opts?: { includeElevator?: boolean },
): string[] => {
  const chips: string[] = [];
  if (listing.property_floor)
    chips.push(`${t('listingCard.floor')} ${listing.property_floor}`);
  if (listing.property_renovation_status) {
    chips.push(getRenovationLabel(listing.property_renovation_status, t));
  }
  if (opts?.includeElevator && listing.property_has_elevator) {
    chips.push(t('exploreListings.elevator', 'Elevator'));
  }
  if (listing.property_has_balcony) chips.push(t('listingCard.balconyTerrace'));
  return chips;
};

/**
 * Builds the click/keyboard props that make a card body openable, or an empty
 * object when no open handler is supplied.
 *
 * The key handler only answers to keys aimed at the card root itself. Cards nest
 * their own controls (share, could buy, manage menu, carousel arrows, view all
 * photos, like), and an Enter/Space bubbling up from one of those used to open
 * the listing *instead of* running the button — preventDefault here cancelled
 * the focused button's own activation (JAA-345).
 */
export const buildCardOpenProps = (
  listing: ListingCardListing,
  onOpen?: (l: ListingCardListing) => void,
) => {
  if (!onOpen) return {};
  return {
    onClick: () => onOpen(listing),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen(listing);
      }
    },
    role: 'button',
    tabIndex: 0,
  };
};

/**
 * Builds the trailing state-modifier class string (selected / own) appended to a
 * card's base className.
 */
export const buildCardStateClass = (
  base: 'listing-card' | 'buyer-card',
  selected: boolean,
  isOwn: boolean,
): string =>
  `${selected ? ` ${base}--selected` : ''}${isOwn ? ` ${base}--own` : ''}`;
